/**
 * Injects global styles into the document's `<head>` tag. If a style tag with the specified ID already exists, it will be replaced.
 *
 * @param globalStyles - The CSS styles to be injected as a string.
 * @param tagId - (Optional) The ID to assign to the `<style>` tag. Defaults to 'ds-foundation' if not provided.
 *
 * @remarks
 * - If a `<style>` tag with the same `tagId` exists, it will be removed before adding the new one.
 * - Ensures that there is always only one `<style>` tag with the given `tagId`.
 *
 * @example
 * ```typescript
 * injectStyles('.example { color: red; }');
 * injectStyles('.another-example { color: blue; }', 'custom-style-tag');
 * ```
 */
function injectStyles(globalStyles, tagId) {
    // Determine the style tag ID, defaulting to 'ds-foundation'
    const styleTagId = tagId || 'ds-foundation';
    // Check if a style tag with the specified ID already exists
    const existingStyleTag = document.head.querySelector(`style#${styleTagId}`);
    if (existingStyleTag) {
        // Remove the existing style tag if found
        document.head.removeChild(existingStyleTag);
    }
    // Create a new style tag
    const styleTag = window.document.createElement('style');
    styleTag.id = styleTagId;
    // Set the content of the style tag to the provided global styles
    styleTag.textContent = globalStyles;
    // Append the style tag to the document's head
    document.head.appendChild(styleTag);
}

export { injectStyles };
